import MainLayout from "@/components/layout/MainLayout"
import { Typography, Row, Col, Collapse } from "antd"
import React, { FC } from "react"
import "./index.less"
const { Title, Paragraph, Text } = Typography
const { Panel } = Collapse

const TermsServiceContent: FC = () => {
  return (
    <MainLayout>
      <div className="terms-service-content">
        <Row className="terms-service-header">
          <Col span={24}>
            <span className="effective-date">Last Updated: May 18, 2025</span>
            <Title level={2}>Terms of Service</Title>
            <Paragraph className="subtitle">
              Please read these Datacord Terms of Service (the "Terms") carefully. They govern your access to and use of Datacord's 
              cloud-based integration platform as a service (iPaaS) available at datacord.com, including Datacord APIs and any updates,
              upgrades, modified versions, extensions, improvements, and derivative works of the foregoing (collectively, the "Service").
            </Paragraph>
          </Col>
        </Row>
        
        <Row gutter={[0, 16]} className="terms-service-sections">
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Introduction</Title>
              <div className="policy-content">
                <Paragraph>
                  Datacord LLC is a Delaware limited liability company, and in these Terms, we will reference ourselves as "Datacord" or "we"/"us".
                </Paragraph>
                <Paragraph>
                  "You" means the entity that you represent in accepting these Terms. You represent and warrant that (i) you have full legal authority to bind your employer or such entity to these Terms; (ii) you have read and understand these Terms; and (iii) you agree to these Terms on behalf of such employer or entity.
                </Paragraph>
                <Paragraph>
                  Your continued use of our Service automatically constitutes your acceptance of these Terms. These Terms completely replace all previous versions.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Definitions</Title>
              <div className="policy-content">
                <ul>
                  <li><Text strong>Account Information:</Text> Information about your Datacord account and information that you and your users provide to Datacord in connection with the creation or administration of your Datacord account.</li>
                  <li><Text strong>Affiliate:</Text> An entity controlled, controlling, or under common control with a party, where control means at least 50% ownership or power to direct an entity's management.</li>
                  <li><Text strong>Beta Features:</Text> Any alpha, beta, developmental, test, experimental, preview, or early-access releases of the Service or other Datacord products or services.</li>
                  <li><Text strong>Customer Content:</Text> Any information, content, or materials that you submit through the Service to your Datacord Account, including from Third-Party Services.</li>
                  <li><Text strong>Documentation:</Text> Datacord-provided user documentation relating to the Service found in our help center, as may be updated by Datacord from time to time.</li>
                  <li><Text strong>Fees:</Text> The fees applicable to your use of the Service in accordance with the applicable subscription plan or pricing tier.</li>
                  <li><Text strong>Intellectual Property Rights:</Text> All patent rights, copyright rights, moral rights, rights of publicity, trademark, trade dress and service mark rights, goodwill, trade secret rights, and other intellectual property rights.</li>
                  <li><Text strong>Third-Party Services:</Text> Products, services, and data you access or import through the Service that are not part of the Service, including any connectors to such third-party products, services, or data.</li>
                </ul>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Account Registration</Title>
              <div className="policy-content">
                <Paragraph>
                  To access and use the Service, you must register for a Datacord account. You must provide accurate account information and promptly update this information if it changes. You are responsible for safeguarding your account credentials and for all activities that occur through your account, regardless of whether the activities are undertaken by you or a third party. You agree to notify Datacord immediately if you believe that your account has been compromised.
                </Paragraph>
                <Paragraph>
                  Datacord reserves the right to disable your account if we determine that you have violated these Terms or if we determine that your account poses a security risk to Datacord, the Service, or any third party.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Use of the Service</Title>
              <div className="policy-content">
                <Paragraph>
                  Subject to these Terms, Datacord grants you a non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service solely in accordance with these Terms and the Documentation. This license is granted solely for your internal business purposes and only for the term of these Terms.
                </Paragraph>
                <Paragraph>
                  You agree to use the Service only in compliance with these Terms, the Documentation, and all applicable laws and regulations. You are solely responsible for your conduct, the content of your Customer Content, and your communications with others while using the Service.
                </Paragraph>
                <Paragraph>
                  You agree not to:
                </Paragraph>
                <ul>
                  <li>Use the Service for any illegal purpose or to violate any laws or regulations;</li>
                  <li>Violate or infringe upon the intellectual property rights, publicity rights, privacy rights, or other rights of any third party;</li>
                  <li>Upload, transmit, or distribute any content that contains viruses, malware, or any other harmful code;</li>
                  <li>Interfere with or disrupt the integrity or performance of the Service or third-party data contained therein;</li>
                  <li>Attempt to gain unauthorized access to the Service or related systems or networks;</li>
                  <li>Use the Service to send unsolicited communications, promotions, or advertisements;</li>
                  <li>Reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code, object code, or underlying structure of the Service, except to the extent that applicable laws prohibit such a restriction;</li>
                  <li>Access the Service to build a competitive product or service, or to benchmark with a competitive product or service;</li>
                  <li>Remove any proprietary notices or labels on the Service or any part, feature, function, or user interface of the Service.</li>
                </ul>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Customer Content</Title>
              <div className="policy-content">
                <Paragraph>
                  You retain all right, title, and interest in and to your Customer Content. You grant Datacord a worldwide, royalty-free license to host, use, store, copy, modify, transmit, and display Customer Content as necessary to provide and maintain the Service and as otherwise permitted in our Privacy Policy.
                </Paragraph>
                <Paragraph>
                  You are solely responsible for your Customer Content and you agree that Datacord is not responsible for any loss, corruption, or other issues related to your Customer Content. You represent and warrant that you have all rights necessary to grant the licenses in this section and that your Customer Content does not violate any law or these Terms.
                </Paragraph>
                <Paragraph>
                  Datacord reserves the right, but is not obligated, to review any Customer Content and, in our sole discretion, delete or remove (without notice) any Customer Content that we determine violates these Terms or might be offensive, illegal, or violate the rights of third parties, or harm or threaten the safety of any person.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Third-Party Services</Title>
              <div className="policy-content">
                <Paragraph>
                  The Service may allow you to access and interoperate with Third-Party Services. These Third-Party Services are not under Datacord's control, and Datacord is not responsible for the content, features, or practices of any Third-Party Services. Your use of Third-Party Services is subject to the terms and conditions of those services.
                </Paragraph>
                <Paragraph>
                  You are solely responsible for reviewing, understanding, and complying with the terms and conditions that apply to your use of any Third-Party Services. If you do not agree to the terms and conditions applicable to the Third-Party Services, you should not use the Third-Party Services.
                </Paragraph>
                <Paragraph>
                  Datacord does not guarantee the continued availability of any integrations with Third-Party Services and may disable any integration at any time at our discretion without notice to you.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Fees and Payment</Title>
              <div className="policy-content">
                <Paragraph>
                  You agree to pay all Fees applicable to your use of the Service. All payment obligations are non-cancelable, and all amounts paid are non-refundable unless explicitly stated otherwise in these Terms or as required by applicable law.
                </Paragraph>
                <Paragraph>
                  If you fail to make any payment when due, Datacord may suspend your access to the Service until such payment is received. Repeated failures to pay may result in termination of your account. You are responsible for all taxes associated with your use of the Service, other than taxes based on Datacord's net income.
                </Paragraph>
                <Paragraph>
                  Datacord may change the Fees for the Service upon thirty (30) days' notice to you. Such notice may be provided by email or by posting the changes on the Datacord website or within the Service. If you continue to use the Service after the Fee change takes effect, you will be deemed to have accepted the new Fees.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Term and Termination</Title>
              <div className="policy-content">
                <Paragraph>
                  These Terms will remain in effect until either you or Datacord terminates them as described in this section.
                </Paragraph>
                <Paragraph>
                  You may terminate these Terms at any time by canceling your account through the Service or by contacting Datacord customer support. Upon termination by you, you will continue to have access to the Service through the end of your current billing period.
                </Paragraph>
                <Paragraph>
                  Datacord may terminate these Terms and your access to the Service for any reason by providing thirty (30) days prior written notice. In addition, Datacord may terminate these Terms immediately if:
                </Paragraph>
                <ul>
                  <li>You breach any provision of these Terms and fail to cure such breach within fifteen (15) days of notice;</li>
                  <li>You go into liquidation, become bankrupt, make a composition with your creditors, have a receiver or administrator appointed over your assets, or cease to operate;</li>
                  <li>Datacord determines that you are using the Service in a way that creates legal liability or disrupts others' use of the Service.</li>
                </ul>
                <Paragraph>
                  Upon termination of these Terms, you must cease all use of the Service. Any sections of these Terms that by their nature should survive termination will survive termination, including but not limited to payment obligations, warranties, indemnification, limitation of liability, and dispute resolution provisions.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Intellectual Property Rights</Title>
              <div className="policy-content">
                <Paragraph>
                  The Service and all materials therein, including, without limitation, the Datacord logo, designs, text, graphics, software, and other files, and the selection, arrangement, and organization thereof, are owned by Datacord or its licensors and are protected by intellectual property laws. Nothing in these Terms gives you any right, title, or interest in or to any of Datacord's Intellectual Property Rights.
                </Paragraph>
                <Paragraph>
                  You acknowledge that any suggestions, ideas, enhancement requests, feedback, recommendations, or other information provided by you to Datacord relating to the Service ("Feedback") is non-confidential and becomes the property of Datacord. Datacord will be entitled to use the Feedback without restriction and without any obligation to you.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Confidentiality</Title>
              <div className="policy-content">
                <Paragraph>
                  Each party may disclose to the other certain Confidential Information. "Confidential Information" means any information disclosed by one party to the other party that is marked as confidential or would normally be considered confidential under the circumstances. Confidential Information does not include information that:
                </Paragraph>
                <ul>
                  <li>Is or becomes generally known to the public without breach of any obligation owed to the disclosing party;</li>
                  <li>Was known to the receiving party prior to its disclosure without breach of any obligation owed to the disclosing party;</li>
                  <li>Was independently developed by the receiving party without breach of any obligation owed to the disclosing party;</li>
                  <li>Is received from a third party without breach of any obligation owed to the disclosing party.</li>
                </ul>
                <Paragraph>
                  Each party agrees to protect the confidentiality of the Confidential Information of the other party in the same manner that it protects the confidentiality of its own proprietary and confidential information of like kind (but in no event using less than reasonable care).
                </Paragraph>
                <Paragraph>
                  A receiving party may disclose Confidential Information to the extent required by law, provided that the receiving party gives the disclosing party prior notice of the disclosure (to the extent permitted by law) and reasonable assistance, at the disclosing party's cost, if the disclosing party wishes to contest the disclosure.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Warranty Disclaimer</Title>
              <div className="policy-content">
                <Paragraph>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF ANY KIND. DATACORD EXPRESSLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </Paragraph>
                <Paragraph>
                  DATACORD MAKES NO WARRANTY THAT THE SERVICE WILL MEET YOUR REQUIREMENTS, THAT THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE, THAT THE RESULTS OBTAINED FROM THE USE OF THE SERVICE WILL BE ACCURATE OR RELIABLE, OR THAT THE QUALITY OF THE SERVICE, INFORMATION, OR OTHER MATERIAL OBTAINED BY YOU THROUGH THE SERVICE WILL MEET YOUR EXPECTATIONS.
                </Paragraph>
                <Paragraph>
                  ANY MATERIAL DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THE SERVICE IS DONE AT YOUR OWN DISCRETION AND RISK. YOU WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM OR OTHER DEVICE OR LOSS OF DATA THAT RESULTS FROM THE DOWNLOAD OF ANY SUCH MATERIAL.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Limitation of Liability</Title>
              <div className="policy-content">
                <Paragraph>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL DATACORD OR ITS AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                </Paragraph>
                <ul>
                  <li>YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE;</li>
                  <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE;</li>
                  <li>ANY CONTENT OBTAINED FROM THE SERVICE; AND</li>
                  <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT;</li>
                </ul>
                <Paragraph>
                  WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE, AND EVEN IF A REMEDY SET FORTH HEREIN IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
                </Paragraph>
                <Paragraph>
                  IN NO EVENT WILL DATACORD'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS, DAMAGES, LOSSES, AND CAUSES OF ACTION ARISING OUT OF OR RELATING TO THESE TERMS OR YOUR USE OF THE SERVICE EXCEED THE AMOUNT PAID BY YOU TO DATACORD FOR THE SERVICE DURING THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Indemnification</Title>
              <div className="policy-content">
                <Paragraph>
                  You agree to defend, indemnify, and hold harmless Datacord, its affiliates, and their respective officers, directors, employees, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from:
                </Paragraph>
                <ul>
                  <li>Your use of and access to the Service;</li>
                  <li>Your violation of any term of these Terms;</li>
                  <li>Your violation of any third-party right, including without limitation any copyright, property, or privacy right;</li>
                  <li>Any claim that your Customer Content caused damage to a third party.</li>
                </ul>
                <Paragraph>
                  This defense and indemnification obligation will survive these Terms and your use of the Service.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Modifications to Terms</Title>
              <div className="policy-content">
                <Paragraph>
                  Datacord may revise these Terms from time to time by posting a modified version of the Terms including their effective date. If we make material changes to the Terms, we will provide you with reasonable notice prior to the new Terms taking effect. By continuing to access or use the Service after the posting of any modified Terms, you agree to be bound by such modified Terms.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">Governing Law and Jurisdiction</Title>
              <div className="policy-content">
                <Paragraph>
                  These Terms will be governed by and construed in accordance with the laws of the State of Delaware, without reference to its conflict of laws principles. All disputes arising out of or relating to these Terms will be submitted to the exclusive jurisdiction of a court of competent jurisdiction located in Wilmington, Delaware, and each party irrevocably consents to such personal jurisdiction and waives all objections to this venue.
                </Paragraph>
                <Paragraph>
                  You acknowledge that by agreeing to these Terms, you and Datacord each waive the right to trial by jury.
                </Paragraph>
              </div>
            </div>
          </Col>
          
          <Col span={24}>
            <div className="policy-category">
              <Title level={3} className="category-title">General Provisions</Title>
              <div className="policy-content">
                <Paragraph>
                  These Terms constitute the entire agreement between you and Datacord concerning your use of the Service and supersede all prior or contemporaneous communications and proposals, whether electronic, oral, or written, between you and Datacord with respect to the Service.
                </Paragraph>
                <Paragraph>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect and enforceable.
                </Paragraph>
                <Paragraph>
                  No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term or any other term, and Datacord's failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.
                </Paragraph>
                <Paragraph>
                  You may not assign or transfer these Terms, by operation of law or otherwise, without Datacord's prior written consent. Any attempt by you to assign or transfer these Terms without such consent will be null. Datacord may freely assign or transfer these Terms without restriction.
                </Paragraph>
              </div>
            </div>
          </Col>
        </Row>
        
        <Row className="terms-service-footer">
          <Col span={24}>
            <Title level={4}>Contact Information</Title>
            <Paragraph>
              If you have any questions about these Terms, please contact us at:
            </Paragraph>
            <Paragraph>
              <Text strong>Datacord LLC</Text><br />
              Email: legal@datacord.com<br />
              Address: 254 Chapman Rd, Ste 208 #16454, Newark, Delaware 19702, USA
            </Paragraph>
          </Col>
        </Row>
      </div>
    </MainLayout>
  )
}

export default TermsServiceContent 